import React from 'react';
import { FormControlLabel, TextField, Tooltip } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import PhoneNumberInput from '@/components/PhoneNumberInput';
import { toE164, toNational } from '@/utilities/formatPhoneNumber';
import { hasPhoneWarning, phoneWarningProps } from '@/utilities/validatePhoneNumber';
import { OnwardCheckbox } from '@/components/ShipmentForm/blocks';
import AddressFields from './AddressFields';
import LocationTypeFields from './LocationTypeFields';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { CheckboxRow, Fieldset, FieldRow, SaveRow } from './blocks';
import { normalizeMaxPallets } from './utils';

const WMS_TOOLTIP =
    'When enabled, this location is available in the Warehouse Management System for inbound and outbound handling.';

const DEFAULT_END_TOOLTIP =
    'When this box is checked, routes created in Planning will automatically have a route end stop set to this location.';

/**
 * The per-location form, shown when a saved or newly added location is selected.
 *
 * Two controls are gated, as before: Location ID appears only when impersonating, and Default
 * End Location only for `saas-v1` clients.
 */
const LocationForm = ({ state, callbacks }) => {
    const { chosen, hasError, viewOnly, isImposter, showDefaultEndLocation } = state;
    const { patch } = callbacks;

    return (
        <>
            {isImposter && (
                <Fieldset columns={2}>
                    <TextField
                        fullWidth
                        disabled
                        variant="outlined"
                        id="location-id"
                        label="Location ID"
                        value={chosen.location_id || ''}
                        InputLabelProps={{ shrink: !!chosen.location_id }}
                    />
                </Fieldset>
            )}

            <Fieldset columns={3}>
                <TextField
                    fullWidth
                    variant="outlined"
                    id="location-name"
                    label="Location Name"
                    value={chosen.location_name || ''}
                    disabled={viewOnly}
                    error={hasError.location_name}
                    InputLabelProps={{ shrink: !!chosen.location_name }}
                    onChange={(e) => patch({ location_name: e.target.value })}
                />
                <TextField
                    fullWidth
                    variant="outlined"
                    id="location-code"
                    label="Location Code"
                    value={chosen.location_code || ''}
                    disabled={viewOnly}
                    error={hasError.location_code}
                    InputLabelProps={{ shrink: !!chosen.location_code }}
                    onChange={(e) => patch({ location_code: e.target.value })}
                />
                {showDefaultEndLocation && (
                    <CheckboxRow>
                        <FormControlLabel
                            control={<OnwardCheckbox />}
                            label="Default End Location"
                            checked={!!chosen.default_end_location}
                            disabled={viewOnly}
                            onChange={callbacks.toggleDefaultEndLocation}
                        />
                        <Tooltip title={DEFAULT_END_TOOLTIP} placement="top">
                            <InfoOutlined fontSize="small" color="action" />
                        </Tooltip>
                    </CheckboxRow>
                )}
            </Fieldset>

            <Fieldset columns={2}>
                <TextField
                    fullWidth
                    variant="outlined"
                    id="location-contact-name"
                    label="Contact Name"
                    value={chosen.contact_name || ''}
                    disabled={viewOnly}
                    error={hasError.contact_name}
                    InputLabelProps={{ shrink: !!chosen.contact_name }}
                    onChange={(e) => patch({ contact_name: e.target.value })}
                />
                <CheckboxRow>
                    <FormControlLabel
                        control={<OnwardCheckbox />}
                        label="Preferred Inbound"
                        checked={!!chosen.preferred_inbound}
                        disabled={viewOnly}
                        onChange={() => patch({ preferred_inbound: !chosen.preferred_inbound })}
                    />
                </CheckboxRow>
            </Fieldset>

            <Fieldset columns={2}>
                <TextField
                    fullWidth
                    variant="outlined"
                    id="location-contact-email"
                    label="Contact Email"
                    value={chosen.contact_email || ''}
                    disabled={viewOnly}
                    error={hasError.contact_email}
                    InputLabelProps={{ shrink: !!chosen.contact_email }}
                    onChange={(e) => patch({ contact_email: e.target.value })}
                />
                <TextField
                    fullWidth
                    variant="outlined"
                    id="location-contact-phone"
                    label="Contact Phone"
                    value={chosen.contact_phone ? toNational(chosen.contact_phone) : ''}
                    disabled={viewOnly}
                    InputProps={{ inputComponent: PhoneNumberInput }}
                    InputLabelProps={{ shrink: !!chosen.contact_phone }}
                    onChange={(e) => patch({ contact_phone: toE164(e.target.value) })}
                    {...phoneWarningProps(hasPhoneWarning(chosen.contact_phone) && !hasError.contact_phone)}
                />
            </Fieldset>

            <FieldRow>
                <CheckboxRow>
                    <FormControlLabel
                        control={<OnwardCheckbox />}
                        label="Enable for Warehouse Management System"
                        checked={!!chosen.enable_for_wms}
                        disabled={viewOnly}
                        onChange={() => patch({ enable_for_wms: !chosen.enable_for_wms })}
                    />
                    <Tooltip title={WMS_TOOLTIP} placement="top">
                        <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                </CheckboxRow>
                {chosen.enable_for_wms && (
                    <TextField
                        variant="outlined"
                        id="location-warehouse-max-pallets"
                        label="Max Pallets"
                        helperText="Maximum pallets this warehouse can hold."
                        value={chosen.warehouse_max_pallets ?? ''}
                        disabled={viewOnly}
                        inputProps={{ inputMode: 'numeric' }}
                        // Raw text while typing, so '12.' keeps its dot and '12.5' can't become 125.
                        // Normalized on blur, and again by the save payloads.
                        onChange={(e) => patch({ warehouse_max_pallets: e.target.value })}
                        onBlur={() => {
                            if (typeof chosen.warehouse_max_pallets === 'string') {
                                patch({ warehouse_max_pallets: normalizeMaxPallets(chosen.warehouse_max_pallets) });
                            }
                        }}
                    />
                )}
            </FieldRow>

            <AddressFields
                chosen={chosen}
                hasError={hasError}
                viewOnly={viewOnly}
                cityLocked={false}
                zipLocked={false}
                onAddressUpdate={callbacks.updateAddress}
                patch={patch}
            />

            <LocationTypeFields chosen={chosen} hasError={hasError} viewOnly={viewOnly} patch={patch} />

            {/*
                An added-but-unsaved location is inserted on its own with ADD_LOCATION, not by the
                bulk client save — so it carries its own Submit, plus a Close that discards it.
            */}
            {state.isNewLocationSelected && !viewOnly && (
                <SaveRow>
                    <PrimaryButton disabled={!state.canSubmitSelected} onClick={callbacks.submitLocation}>
                        Submit
                    </PrimaryButton>
                    <SecondaryButton onClick={callbacks.discardNewLocation}>Close</SecondaryButton>
                </SaveRow>
            )}
        </>
    );
};

export default LocationForm;
